import api from '@/config/api'
import { DashboardOverview, TimeSeriesData, FeatureUsageData, InvitationStatistics } from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5004/api';

export const analyticsService = {
   // Get dashboard metrics
   async getDashboardMetrics() {
      const response = await api.get(`${API_URL}/analytics/dashboard-metrics`);
      return response.data.data;
   },

   // Get dashboard overview data
   async getOverview(): Promise<DashboardOverview> {
      const response = await api.get(`${API_URL}/analytics/overview`);
      return response.data.data;
   },

   // Get time series data
   async getTimeSeries(params: {
      metric: string;
      period?: string;
      startDate?: string;
      endDate?: string;
   }): Promise<{ data: TimeSeriesData[]; metadata: any }> {
      const response = await api.get(`${API_URL}/analytics/time-series`, { params });
      return response.data;
   },

   // Get new users with details
   async getNewUsers(period: string = '7d'): Promise<{
      users: Array<{
         id: string;
         fullName: string;
         email: string;
         photoURL: string;
         createdAt: string;
         accountType: string;
         lastLogin: string | null;
         displayName: string;
      }>;
      summary: {
         totalUsers: number;
         period: string;
         averageUsersPerDay: number;
         mostActiveDay: { date: string; count: number } | null;
         dateRange: { start: string; end: string };
      };
   }> {
      const response = await api.get(`${API_URL}/analytics/new-users`, { params: { period } });
      return response.data.data;
   },

   // Get new albums with details
   async getNewAlbums(period: string = '7d'): Promise<{
      albums: Array<{
         id: string;
         title: string;
         description: string;
         createdBy: string;
         createdAt: string;
         isPublic: boolean;
         memberIds: string[];
         photoIds: string[];
         invitedEmails: string[];
         user: {
            id: string;
            fullName: string;
            email: string;
            photoURL: string;
         };
         totalMembers: number;
         totalPhotos: number;
         totalInvited: number;
      }>;
      summary: {
         totalAlbums: number;
         period: string;
         averageAlbumsPerDay: number;
         mostActiveDay: { date: string; count: number } | null;
         dateRange: { start: string; end: string };
      };
   }> {
      const response = await api.get(`${API_URL}/analytics/new-albums`, { params: { period } });
      return response.data.data;
   },

   // Get new photos with details
   async getNewPhotos(period: string = '7d'): Promise<{
      photos: Array<{
         id: string;
         userId: string;
         originalUrl: string;
         enhancedUrl: string;
         thumbnailUrl: string;
         createdAt: string;
         isPublic: boolean;
         isShared: boolean;
         description: string;
         tags: string[];
         tripId: string | null;
         albumId: string | null;
         eventId: string | null;
         user: {
            id: string;
            fullName: string;
            email: string;
            photoURL: string;
         };
      }>;
      summary: {
         totalPhotos: number;
         period: string;
         averagePhotosPerDay: number;
         mostActiveDay: { date: string; count: number } | null;
         dateRange: { start: string; end: string };
      };
   }> {
      const response = await api.get(`${API_URL}/analytics/new-photos`, { params: { period } });
      return response.data.data;
   },

   // Get new trips with details
   async getNewTrips(period: string = '7d'): Promise<{
      trips: Array<{
         id: string;
         title: string;
         description: string;
         createdBy: string;
         createdAt: string;
         startDate: string | null;
         endDate: string | null;
         isPublic: boolean;
         isShared: boolean;
         location: any;
         participants: string[];
         tags: string[];
         status: string;
         user: {
            id: string;
            fullName: string;
            email: string;
            photoURL: string;
         };
         totalParticipants: number;
      }>;
      summary: {
         totalTrips: number;
         period: string;
         averageTripsPerDay: number;
         mostActiveDay: { date: string; count: number } | null;
         dateRange: { start: string; end: string };
      };
   }> {
      const response = await api.get(`${API_URL}/analytics/new-trips`, { params: { period } });
      return response.data.data;
   },

   // Get new events with details
   async getNewEvents(period: string = '7d'): Promise<{
      events: Array<{
         id: string;
         title: string;
         description: string;
         createdBy: string;
         createdAt: string;
         eventDate: string | null;
         isPublic: boolean;
         isShared: boolean;
         location: any;
         attendees: string[];
         tags: string[];
         status: string;
         user: {
            id: string;
            fullName: string;
            email: string;
            photoURL: string;
         };
         totalAttendees: number;
      }>;
      summary: {
         totalEvents: number;
         period: string;
         averageEventsPerDay: number;
         mostActiveDay: { date: string; count: number } | null;
         dateRange: { start: string; end: string };
      };
   }> {
      const response = await api.get(`${API_URL}/analytics/new-events`, { params: { period } });
      return response.data.data;
   },

   // Get feature usage data
   async getFeatureUsage(feature: string, period: string): Promise<FeatureUsageData> {
      const response = await api.get(`${API_URL}/analytics/feature-usage`, {
         params: { feature, period },
      });
      return response.data.data;
   },

   // Get geographic data
   async getGeographicData(metric: string, period: string, limit: number = 10) {
      const response = await api.get(`${API_URL}/analytics/geographic`, {
         params: { metric, period, limit },
      });
      return response.data.data;
   },

   // Get user paths data (page views)
   async getUserPaths(days: number = 30) {
      const response = await api.get(`${API_URL}/analytics/report/page-views`, {
         params: { days },
      });
      return response.data;
   },

   // Get detailed user visited paths with analytics
   async getUserVisitedPaths(days: number = 30, limit: number = 50, offset: number = 0) {
      const response = await api.get(`${API_URL}/analytics/user-paths`, {
         params: { days, limit, offset },
      });
      return response.data;
   },

   // Get most recent user visited paths with date information
   async getRecentUserPaths(
      limit: number = 50,
      offset: number = 0,
      forceRefresh: boolean = false,
      payload: { startDate: string; endDate: string }
   ) {
      const response = await api.get(`${API_URL}/analytics/engagement-analytics`, {
         params: {
            limit,
            offset,
            forceRefresh,
            startDate: payload.startDate,
            endDate: payload.endDate,
         },
      });
      return response.data;
   },

   // Get invitation statistics
   async getInvitationStatistics(period: '9' | '30' | '90'): Promise<InvitationStatistics> {
      const response = await api.get(`${API_URL}/analytics/invitation-statistics`, {
         params: { period },
      });
      return response.data.data;
   },
}; 